"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { UserIdentity } from "@supabase/supabase-js";
import { buildAuthFlowRedirectUrl } from "@/lib/auth/redirect-target";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { buildPublicSiteUrl } from "@/lib/site-url";
import { useAccountData } from "./account-data-context";

const providerOptions = [
  {
    id: "google",
    label: "Google",
    icon: "/icons/oauth/google.png",
  },
  {
    id: "github",
    label: "GitHub",
    icon: "/icons/oauth/github.png",
  },
] as const;

type OAuthProvider = (typeof providerOptions)[number]["id"];

const providerLabels: Record<string, string> = {
  google: "Google",
  github: "GitHub",
  email: "Email",
};

function formatProviderLabel(provider: string) {
  return (
    providerLabels[provider] ??
    provider.replace(/(^|\s|_)([a-z])/g, (match, prefix, char) =>
      `${prefix}${char.toUpperCase()}`
    )
  );
}

function getProviderIcon(provider: string) {
  return providerOptions.find((option) => option.id === provider)?.icon ?? null;
}

function EmailIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 text-[var(--muted)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6.5h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" />
      <path d="m3 8 9 6 9-6" />
    </svg>
  );
}

function PasswordIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 text-[var(--muted)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export default function AuthSettingsPanel() {
  const supabase = getSupabaseBrowserClient();
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    email,
    identities,
    hasPassword,
    hasMagicLink,
    isLoading,
    refreshIdentities,
    setHasMagicLink,
    setHasPassword,
  } = useAccountData();
  const [status, setStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const linkedProviders = useMemo(() => {
    const providers = new Set(identities.map((identity) => identity.provider));

    if (hasPassword || hasMagicLink) {
      providers.add("email");
    }

    return providers;
  }, [hasMagicLink, hasPassword, identities]);

  const hasEmailMethod =
    hasMagicLink || hasPassword || linkedProviders.has("email");

  const linkedMethods = useMemo(() => {
    const ordered: string[] = [];

    if (hasEmailMethod) {
      ordered.push("email");
    }
    if (hasPassword) {
      ordered.push("password");
    }

    providerOptions.forEach((provider) => {
      if (linkedProviders.has(provider.id)) {
        ordered.push(provider.id);
      }
    });

    linkedProviders.forEach((provider) => {
      if (!ordered.includes(provider) && provider !== "email") {
        ordered.push(provider);
      }
    });

    return ordered;
  }, [hasEmailMethod, hasPassword, linkedProviders]);

  const canUnlink = identities.length > 1;
  const hasEmailIdentity = identities.some(
    (identity) => identity.provider === "email"
  );
  const hasWeakPasswordStatus = Boolean(searchParams.get("weak_password"));
  const linkedStatus = useMemo(() => {
    if (hasWeakPasswordStatus) {
      return "Your current password no longer meets the account policy. Reset it to a stronger password.";
    }
    const linked = searchParams.get("linked");
    return linked ? `${formatProviderLabel(linked)} linked.` : null;
  }, [hasWeakPasswordStatus, searchParams]);

  useEffect(() => {
    const linked = searchParams.get("linked");
    if (linked) {
      if (linked === "email") {
        void (async () => {
          const { data, error } = await supabase.auth.getUser();
          if (error || !data?.user) {
            return;
          }
          const nextMetadata = {
            ...data.user.user_metadata,
            has_magic_link: true,
          };
          await supabase.auth.updateUser({ data: nextMetadata });
          setHasMagicLink(true);
        })();
      }
      const timer = window.setTimeout(() => {
        router.replace("/account/settings");
      }, 1200);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [router, searchParams, setHasMagicLink, supabase]);

  const handleLinkProvider = async (provider: OAuthProvider) => {
    setActiveAction(`link-${provider}`);
    setStatus(`Connecting ${formatProviderLabel(provider)}...`);
    setErrorMessage(null);

    const redirectTo = buildPublicSiteUrl(
      `/auth/callback?next=/account/settings&linked=${provider}`,
      window.location.origin,
    );
    const { data, error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: true,
      },
    });

    if (error) {
      setErrorMessage(error.message);
      setStatus(null);
      setActiveAction(null);
      return;
    }

    if (!data?.url) {
      setErrorMessage("Unable to start linking.");
      setStatus(null);
      setActiveAction(null);
      return;
    }

    window.location.assign(data.url);
  };

  const handleUnlinkProvider = async (identity: UserIdentity) => {
    if (!canUnlink) {
      setErrorMessage("Link another method before removing this one.");
      return;
    }

    setActiveAction(`unlink-${identity.provider}`);
    setStatus(`Removing ${formatProviderLabel(identity.provider)}...`);
    setErrorMessage(null);

    const { error } = await supabase.auth.unlinkIdentity(identity);

    if (error) {
      setErrorMessage(error.message);
      setStatus(null);
      setActiveAction(null);
      return;
    }

    await refreshIdentities();
    setStatus(`${formatProviderLabel(identity.provider)} removed.`);
    setActiveAction(null);
  };

  const handlePasswordUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!newPassword || !confirmPassword) {
      setErrorMessage("Enter and confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setActiveAction("password");
    setStatus("Updating password...");

    let nextMetadata: Record<string, unknown> = { has_password: true };
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (!userError && userData?.user?.user_metadata) {
      nextMetadata = {
        ...userData.user.user_metadata,
        has_magic_link: userData.user.user_metadata.has_magic_link,
        has_password: true,
      };
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      data: nextMetadata,
    });

    if (error) {
      setErrorMessage(error.message);
      setStatus(null);
      setActiveAction(null);
      return;
    }

    setNewPassword("");
    setConfirmPassword("");
    setHasPassword(true);
    setActiveAction(null);
    setStatus("Password updated.");
  };

  const handleMagicLink = async () => {
    if (!email) {
      setErrorMessage("No email found yet.");
      return;
    }

    setActiveAction("magic");
    setStatus("Sending magic link...");
    setErrorMessage(null);

    const redirectTo = buildAuthFlowRedirectUrl(
      "/account/settings?linked=email",
      "magic_link_settings",
      window.location.origin,
    );
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        shouldCreateUser: false,
      },
    });

    setActiveAction(null);

    if (error) {
      setStatus(null);
      setErrorMessage(error.message);
      return;
    }

    setStatus("Check your email.");
  };

  return (
    <section className="panel space-y-5 p-6">
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-[var(--muted)]">
          Authentication
        </p>
      </div>

      {(status || errorMessage || linkedStatus) && (
        <div
          className={`rounded-2xl border px-4 py-3 text-xs uppercase tracking-[0.3em] ${
            errorMessage
              ? "border-red-400/40 text-red-200"
              : hasWeakPasswordStatus
                ? "border-amber-300/50 bg-amber-300/10 text-amber-100"
              : "border-emerald-400/40 text-emerald-200"
          }`}
          role={errorMessage ? "alert" : "status"}
        >
          {errorMessage ?? status ?? linkedStatus}
        </div>
      )}

      <div className="space-y-3">
        <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
          Linked methods
        </p>
        {isLoading && (
          <p className="text-sm text-[var(--muted)]">Loading...</p>
        )}
        {!isLoading && linkedMethods.length === 0 && (
          <p className="text-sm text-[var(--muted)]">None linked.</p>
        )}
        {!isLoading && linkedMethods.length > 0 && (
          <div className="space-y-3">
            {linkedMethods.map((method) => {
              const providerLabel =
                method === "password"
                  ? "Password"
                  : method === "email"
                    ? "Email"
                    : formatProviderLabel(method);
              const providerIcon = getProviderIcon(method);
              const isEmailMethod = method === "email";
              const isPasswordMethod = method === "password";
              const identity = identities.find(
                (item) => item.provider === method
              );
              const isOAuthProvider = providerOptions.some(
                (option) => option.id === method
              );

              return (
                <div
                  key={identity?.id ?? method}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[var(--bg)] px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    {providerIcon ? (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <Image
                          src={providerIcon}
                          alt=""
                          width={20}
                          height={20}
                          className="h-5 w-5 brightness-0 invert"
                        />
                      </span>
                    ) : isEmailMethod ? (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <EmailIcon />
                      </span>
                    ) : isPasswordMethod ? (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <PasswordIcon />
                      </span>
                    ) : (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-xs font-semibold uppercase text-[var(--muted)]">
                        {providerLabel.slice(0, 2)}
                      </span>
                    )}
                    <p className="text-sm font-semibold text-[var(--text)]">
                      {providerLabel}
                    </p>
                  </div>
                  {isOAuthProvider ? (
                    <button
                      type="button"
                      disabled={!canUnlink || !identity}
                      onClick={() =>
                        identity ? handleUnlinkProvider(identity) : null
                      }
                      className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--text)] transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {activeAction === `unlink-${method}`
                        ? "Removing..."
                        : "Remove"}
                    </button>
                  ) : (
                    <span className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                      On
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
          Add method
        </p>
        <div className="grid gap-3">
          {providerOptions.map((provider) => {
            const isLinked = linkedProviders.has(provider.id);
            const isActive =
              activeAction === `link-${provider.id}` || isLinked;

            return (
              <button
                key={provider.id}
                type="button"
                onClick={() => handleLinkProvider(provider.id)}
                disabled={isActive}
                className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-[var(--text)] transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                    <Image
                      src={provider.icon}
                      alt=""
                      width={20}
                      height={20}
                      className="h-5 w-5 brightness-0 invert"
                    />
                  </span>
                  {provider.label}
                </span>
                <span className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                  {isLinked ? "Linked" : "Link"}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleMagicLink}
            disabled={
              hasMagicLink || hasEmailIdentity || activeAction === "magic"
            }
            className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-[var(--text)] transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-xs font-semibold uppercase text-[var(--muted)]">
                <EmailIcon />
              </span>
              Email magic link
            </span>
            <span className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              {hasMagicLink || hasEmailIdentity ? "Linked" : "Link"}
            </span>
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[var(--bg)] p-4">
        <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
          Password
        </p>
        {hasPassword ? (
          <Link
            href="/reset-password"
            className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-[var(--text)] transition hover:border-white/40"
          >
            Reset password
          </Link>
        ) : (
          <form className="mt-3 space-y-4" onSubmit={handlePasswordUpdate}>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              New password
              <input
                type="password"
                name="new-password"
                placeholder="New password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
              />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              Confirm password
              <input
                type="password"
                name="confirm-password"
                placeholder="Confirm password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
              />
            </label>
            <button
              type="submit"
              disabled={activeAction === "password"}
              className="w-full rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
            >
              {activeAction === "password" ? "Updating..." : "Set password"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
